import { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { Form, Field } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import { FORM_ERROR } from 'final-form'
import arrayMutators from 'final-form-arrays'
import dayjs from 'dayjs'

import { Page } from '../elements'
import {
  Loading,
  CurrencyInput,
  TextInput,
  Button,
  MultiButtonInput,
  HelpIcon,
  TooltipText,
  TextErrorField,
  YearPicker,
} from '../../../components'
import { reduceValidationError, withRouterProps } from '../../../utils'
import {
  schema,
  calculator,
  initialize,
  calcTotalRemaining,
  isFundingClosed,
  prepareValuesForSubmit,
} from './GoalUtils'

class GoalForm extends Component {
  onSubmit = async values => {
    const requestValues = prepareValuesForSubmit(values)
    try {
      const {
        navigate,
        store: { updateGoal, addGoal },
        isEdit,
        handleClose,
      } = this.props

      if (isEdit) {
        await updateGoal(requestValues)
        handleClose()
      } else {
        await addGoal(requestValues)
        navigate('/goals')
      }
    } catch (err) {
      console.error(err)
      return { [FORM_ERROR]: 'Oops! Something went wrong, please try again later' }
    }
  }

  validate = values => {
    const {
      store,
      match: { params },
      isEdit,
    } = this.props
    const existingGoalNames = store.goalNames(params.id)
    // Allow a zero remaining amount when the current year's funding has been withdrawn.
    const goalNoLongerFundable = isEdit && isFundingClosed(values.endDate, values.paid)
    const totalRemainingValue = calcTotalRemaining(values)
    const totalRemaining =
      (totalRemainingValue > 0 && totalRemainingValue <= 100000000) || goalNoLongerFundable
        ? undefined
        : 'Amount must be from $1 to $100,000,000'
    return schema(existingGoalNames)
      .validate(values, { abortEarly: false })
      .then(() => ({ totalRemaining }))
      .catch(err => ({ ...reduceValidationError(err), totalRemaining }))
  }

  initialValues = () => {
    const { isEdit, foundGoal } = this.props
    return initialize({ foundGoal, isEdit })
  }

  render() {
    const { isEdit, foundGoal, handleClose, readOnly = false } = this.props

    return (
      <Page.Container margin={isEdit ? '0' : '0 auto'}>
        <Form
          onSubmit={this.onSubmit}
          validate={this.validate}
          mutators={{ ...arrayMutators }}
          decorators={[calculator]}
          initialValues={this.initialValues()}
          subscription={{ submitting: true, submitError: true, values: true }}
          render={({ handleSubmit, submitting, submitError, values, form }) => {
            return (
              <Page.FormBox>
                <GoalName readOnly={readOnly} />
                <GoalType values={values} readOnly={readOnly} />

                <YearNeeded values={values} readOnly={readOnly} />
                <YearsNeeded
                  values={values}
                  form={form}
                  readOnly={readOnly}
                  foundGoal={isEdit ? foundGoal : undefined}
                />
                <SameAmount values={values} readOnly={readOnly} />
                <AmountPerYear values={values} readOnly={readOnly} />
                <Amounts values={values} readOnly={readOnly} />
                <TotalRemaining values={values} />
                <PaidField values={values} isEdit={isEdit} readOnly={readOnly} />
                <Page.ErrorMessage>{!submitting && submitError}</Page.ErrorMessage>

                <Field
                  name='totalRemaining'
                  subscription={{ touched: true, error: true }}
                  render={({ meta: { error, touched } }) => {
                    return (
                      <TextErrorField
                        error={error}
                        showError={
                          (values.isOneTime === true ||
                            (values.isOneTime === false && values.sameAmount !== null)) &&
                          touched
                        }
                      />
                    )
                  }}
                />

                {submitting ? (
                  <Loading />
                ) : readOnly ? (
                  <Page.ButtonsBox maxWidth='800px' justifyContent='flex-start'>
                    <Button variant='secondary' onClick={handleClose}>
                      Close
                    </Button>
                  </Page.ButtonsBox>
                ) : (
                  <Page.ButtonsBox maxWidth='800px'>
                    <Page.LeftButtonBox>
                      <Button variant='secondary' onClick={handleClose}>
                        Cancel
                      </Button>
                    </Page.LeftButtonBox>
                    <Page.RightButtonBox>
                      <Button variant='primary' onClick={handleSubmit}>
                        Save
                      </Button>
                    </Page.RightButtonBox>
                  </Page.ButtonsBox>
                )}
              </Page.FormBox>
            )
          }}
        />
      </Page.Container>
    )
  }
}

const GoalName = ({ readOnly }) => (
  <Page.FlexRow>
    <Page.Label>Goal name</Page.Label>

    <Field
      name='name'
      format={v => v}
      parse={v => v}
      subscription={{ value: true, touched: true, error: true }}
      render={({ input, meta }) => (
        <Page.ValueBox>
          <TextInput
            name={input.name}
            value={input.value}
            onChange={(name, value) => input.onChange(value)}
            onBlur={input.onBlur}
            expanded={input.value}
            error={meta.error}
            showError={meta.touched || (input.value || '').length > 32}
            disabled={readOnly}
          />
        </Page.ValueBox>
      )}
    />
  </Page.FlexRow>
)

const GoalType = ({ values, readOnly }) => (
  <Page.FlexRow>
    <Page.Label>Goal type</Page.Label>
    <Page.ValueBox>
      <Field
        name='isOneTime'
        subscription={{ value: true, touched: true, error: true }}
        render={({ input, meta }) => (
          <MultiButtonInput
            name={input.name}
            value={input.value}
            onChange={(name, value) => input.onChange(value)}
            onBlur={input.onBlur}
            buttons={[
              { text: 'One-Time', value: true },
              { text: 'Multi-year', value: false },
            ]}
            error={meta.error}
            showError={meta.touched}
            disabled={readOnly}
          />
        )}
      />
    </Page.ValueBox>
    {values.isOneTime === false && (
      <Page.FieldHelper>
        Please do not include regular expenses, such as <br /> insurance premiums and mortgage
        payments.
        <span>
          <HelpIcon
            size='medium'
            tooltip={TooltipText.whatIsAGoal()}
            style={{ marginLeft: '5px' }}
          />
        </span>
      </Page.FieldHelper>
    )}
  </Page.FlexRow>
)

const YearNeeded = ({ values, readOnly }) =>
  values.isOneTime === true ? (
    <Page.FlexRow>
      <Page.Label>Year needed</Page.Label>

      <Page.ValueBox>
        <Field
          name='startDate'
          render={({ input }) => (
            <YearPicker
              name={input.name}
              value={input.value}
              onChange={(name, value) => input.onChange(value)}
              minYear={values.minYear}
              maxYear={dayjs().add(49, 'year').year()}
              disabled={readOnly}
            />
          )}
        />
      </Page.ValueBox>
    </Page.FlexRow>
  ) : null

const YearsNeeded = ({ values, form, readOnly, foundGoal }) => {
  const startYear = dayjs(values.startDate).year()
  // Goals saved before the ten-year cap can hold an eleventh year. Keep that
  // year selectable, or editing one drops the end-year dropdown back to its
  // placeholder and loses the stored value. The reprieve only applies while
  // the start year is untouched: moving it re-bases the range, so the cap
  // applies from the new start year instead of stretching past it.
  const savedStartYear = foundGoal && dayjs(foundGoal.startDate).year()
  const savedEndYear = foundGoal && dayjs(foundGoal.endDate).year()
  const maxEndYear =
    savedStartYear === startYear ? Math.max(startYear + 9, savedEndYear) : startYear + 9

  return values.isOneTime === false ? (
    <Page.FlexRow>
      <Page.Label>Years needed</Page.Label>

      <Page.ValueBox>
        <Page.Row>
          <Field
            name='startDate'
            render={({ input }) => (
              <YearPicker
                name={input.name}
                value={input.value}
                onChange={(name, value) => {
                  input.onChange(value)
                  const endDate = dayjs(value).endOf('year').format('YYYY-MM-DD')
                  form.change('endDate', endDate)
                }}
                minYear={values.minYear}
                maxYear={dayjs().add(49, 'year').year()}
                disabled={readOnly}
              />
            )}
          />
          <Page.SpanText>to&nbsp;</Page.SpanText>
          <Field
            name='endDate'
            render={({ input }) => (
              <YearPicker
                name={input.name}
                value={input.value}
                onChange={(name, value) =>
                  input.onChange(dayjs(value).endOf('year').format('YYYY-MM-DD'))
                }
                minYear={startYear}
                maxYear={maxEndYear}
                disabled={readOnly || !startYear}
              />
            )}
          />
        </Page.Row>
      </Page.ValueBox>
      <Page.FieldHelper>Limited to no more than 10 years of payments.</Page.FieldHelper>
    </Page.FlexRow>
  ) : null
}

const SameAmount = ({ values, readOnly }) =>
  values.isOneTime === false ? (
    <Page.FlexRow>
      <Page.Label>Is the amount needed the same each year?</Page.Label>
      <Page.ValueBox>
        <Field
          name='sameAmount'
          subscription={{ value: true, touched: true, error: true }}
          render={({ input, meta }) => (
            <MultiButtonInput
              name={input.name}
              value={input.value}
              onChange={(name, value) => input.onChange(value)}
              onBlur={input.onBlur}
              buttons={[
                { text: 'Same', value: true },
                { text: 'Varies', value: false },
              ]}
              error={meta.error}
              showError={meta.touched}
              disabled={readOnly}
            />
          )}
        />
      </Page.ValueBox>
    </Page.FlexRow>
  ) : null

const AmountPerYear = ({ values, readOnly }) =>
  values.isOneTime === true || (values.isOneTime === false && values.sameAmount === true) ? (
    <Page.FlexRow>
      <Page.Label>
        {values.isOneTime === true ? 'Future total needed' : 'Future amount needed per year'}
      </Page.Label>

      <Page.YearRow>
        <Field
          name='amountPerYear'
          format={value => (value === null ? undefined : value)}
          parse={v => v}
          validate={value => (!value ? 'Please enter an amount' : undefined)}
          render={({ input, meta }) => (
            <CurrencyInput
              name={input.name}
              value={input.value}
              placeholder='amount'
              onChange={(name, value) => input.onChange(value)}
              allowNegative={false}
              decimalPrecision={0}
              onBlur={input.onBlur}
              error={meta.error}
              showError={meta.touched}
              disabled={readOnly}
            />
          )}
        />
      </Page.YearRow>
    </Page.FlexRow>
  ) : null

const Amounts = ({ values, readOnly }) =>
  values.isOneTime === false && values.sameAmount === false ? (
    <FieldArray name='amounts'>
      {({ fields }) => (
        <Page.FlexRow>
          <Page.Label>Future amount needed per year</Page.Label>
          {fields.map(name => (
            <Field
              key={name}
              name={name}
              render={({ input }) => (
                <Page.YearRow key={input.value.year}>
                  <Page.YearLabel>{input.value.year}</Page.YearLabel>
                  <Field
                    name={`${name}.amount`}
                    format={value => (value === null ? undefined : value)}
                    parse={v => v}
                    validate={value => (!value ? 'Please enter an amount' : undefined)}
                    render={({ input, meta }) => (
                      <CurrencyInput
                        name={input.name}
                        value={input.value}
                        placeholder='amount'
                        onChange={(name, value) => input.onChange(value)}
                        allowNegative={false}
                        decimalPrecision={0}
                        onBlur={input.onBlur}
                        error={meta.error}
                        showError={meta.touched}
                        disabled={readOnly}
                      />
                    )}
                  />
                </Page.YearRow>
              )}
            />
          ))}
        </Page.FlexRow>
      )}
    </FieldArray>
  ) : null

const TotalRemaining = ({ values }) =>
  values.isOneTime === false && values.sameAmount !== null ? (
    <Page.FlexRow>
      <Page.Label>Future total needed</Page.Label>
      <Page.ValueBox>
        <CurrencyInput
          name='totalRemaining'
          value={calcTotalRemaining(values)}
          onChange={() => {}}
          readonly
          disabled
        />
      </Page.ValueBox>
    </Page.FlexRow>
  ) : null

const PaidField = ({ values, isEdit, readOnly }) =>
  isEdit && dayjs().isBetween(values.startDate, values.endDate, 'year', '[]') ? (
    <Page.FlexRow>
      <Page.Label>
        {values.isOneTime === true
          ? 'Has the amount needed been withdrawn?'
          : 'Has the amount needed for this year been withdrawn?'}
        <Page.SVGHelpContainer>
          <HelpIcon size='medium' tooltip={TooltipText.hasAmountBeenWithdrawn()} />
        </Page.SVGHelpContainer>
      </Page.Label>

      <Page.ValueBox>
        <Field
          name='paid'
          subscription={{ value: true, touched: true, error: true }}
          render={({ input, meta }) => (
            <MultiButtonInput
              name={input.name}
              value={input.value}
              onChange={(_, value) => input.onChange(value)}
              onBlur={input.onBlur}
              buttons={[
                { text: 'Yes', value: true },
                { text: 'No', value: false },
              ]}
              error={meta.error}
              showError={meta.touched}
              disabled={readOnly}
            />
          )}
        />
      </Page.ValueBox>
    </Page.FlexRow>
  ) : null

export default withRouterProps(inject('store')(observer(GoalForm)))
